import { ChangeDetectionStrategy, Component, ElementRef, inject, input, output, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ToastService } from '../../../../../../services/toast.service';
import { GraphMeasure } from '../../../../../../types/graph-data.interface';
import { YearType } from '../../../../../../types/graph.interface';
import { MeasureChipComponent } from '../measure-chip.component';

const YEAR_PATTERN = /^(\d{4}|[AaNn](-[1-9]\d*)?)$/;

@Component({
  selector: 'app-measure-selector-multi-year',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, RadioButtonModule, ChipModule, InputTextModule, ButtonModule, MeasureChipComponent, TranslocoModule],
  templateUrl: './measure-selector-multi-year.component.html',
})
export class MeasureSelectorMultiYearComponent {
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslocoService);

  readonly selectedMeasure = input.required<GraphMeasure | null>();
  readonly yearType = input.required<YearType>();
  readonly availableYears = input.required<string[]>();
  readonly isYearTypeLocked = input(false);

  readonly addMeasure = output<void>();
  readonly configureMeasure = output<void>();
  readonly removeMeasure = output<void>();
  readonly yearTypeChange = output<YearType>();
  readonly addYear = output<string>();
  readonly removeYear = output<string>();

  readonly yearInput = signal('');
  readonly errorMessage = signal('');
  readonly yearInputElement = viewChild<ElementRef<HTMLInputElement>>('yearInputElement');

  getYearChipAria(year: string): string {
    return this.translate.translate('graphs.multiYear.yearChipAria', { year });
  }

  focusInput(): void {
    const inputEl = this.yearInputElement();
    if (inputEl) {
      inputEl.nativeElement.focus();
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    const input = this.yearInput().trim();

    if (event.key === ' ' || event.key === ',' || event.key === 'Enter') {
      if (input) {
        event.preventDefault();

        const normalizedInput = this.normalizeYear(input);

        if (YEAR_PATTERN.test(normalizedInput)) {
          const currentYears = this.availableYears();

          if (!currentYears.includes(normalizedInput)) {
            this.addYear.emit(normalizedInput);
            this.toastService.success('graphs.multiYear.yearAdded', undefined, undefined, undefined, { year: normalizedInput });
          } else {
            this.toastService.info('graphs.multiYear.yearAlreadyPresent', undefined, undefined, undefined, { year: normalizedInput });
          }

          this.yearInput.set('');
          this.errorMessage.set('');
        } else {
          this.errorMessage.set(this.translate.translate('graphs.multiYear.invalidFormat'));
        }
      }
    }

    if (event.key === 'Backspace' && !input) {
      const years = this.availableYears();
      const lastYear = years[years.length - 1];
      if (lastYear) {
        event.preventDefault();
        this.removeYear.emit(lastYear);
        this.toastService.info('graphs.multiYear.yearRemoved', undefined, undefined, undefined, { year: lastYear });
      }
    }
  }

  private normalizeYear(year: string): string {
    if (year.toLowerCase().startsWith('n') || year.toLowerCase().startsWith('a')) {
      return 'A' + year.substring(1);
    }
    return year;
  }

  onAddYear(): void {
    const input = this.yearInput().trim();
    if (!input) return;

    const years = input
      .split(/[,\s]+/)
      .filter(y => y.length > 0)
      .map(y => this.normalizeYear(y));

    const invalidYears = years.filter(year => !YEAR_PATTERN.test(year));
    if (invalidYears.length > 0) {
      this.errorMessage.set(this.translate.translate('graphs.multiYear.invalidFormatList', { years: invalidYears.join(', ') }));
      return;
    }

    const currentYears = this.availableYears();
    const newYears = years.filter(year => !currentYears.includes(year));
    const duplicates = years.filter(year => currentYears.includes(year));

    this.errorMessage.set('');
    newYears.forEach(year => this.addYear.emit(year));
    this.yearInput.set('');

    if (newYears.length > 0) {
      const message =
        newYears.length === 1
          ? this.translate.translate('graphs.multiYear.yearAdded', { year: newYears[0] })
          : this.translate.translate('graphs.multiYear.yearsAdded', { count: newYears.length, years: newYears.join(', ') });
      this.toastService.success(message);
    }

    if (duplicates.length > 0) {
      const dupMsg =
        duplicates.length === 1
          ? this.translate.translate('graphs.multiYear.yearAlreadyPresent', { year: duplicates[0] })
          : this.translate.translate('graphs.multiYear.yearsAlreadyPresent', { count: duplicates.length });
      this.toastService.info(dupMsg);
    }
  }
}
